
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Info, Calendar as CalendarIcon, Maximize2, Minimize2, Eye, EyeOff, BarChart3, LayoutGrid, X, Activity, PieChart, Search, Minus, Plus, RotateCcw, Medal, ArrowRight } from 'lucide-react';
import { Transaction, Candle } from '../types';
import { fetchBinanceKlines, PriceData } from '../services/marketService';

interface DailyReturn {
  date: string;
  timestamp: number;
  value: number;
  cost: number;
  pnl: number;
  roiPerc: number;
  heldTickers: string[]; 
}

type ViewMode = 'calendar' | 'histogram';
type CalendarStructure = 'days' | 'months'; 
type Granularity = 'monthly' | 'yearly'; 

interface PortfolioCalendarProps {
  transactions: Transaction[];
  theme: 'dark' | 'light';
  isMaximized?: boolean;
  onToggleMaximize?: (val: boolean) => void;
  icons?: Record<string, string>;
  currentPrices?: Record<string, PriceData>;
}

const PortfolioCalendar: React.FC<PortfolioCalendarProps> = ({ transactions, theme, isMaximized, onToggleMaximize, icons = {}, currentPrices = {} }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [calendarStructure, setCalendarStructure] = useState<CalendarStructure>('days');
  const [histogramGranularity, setHistogramGranularity] = useState<Granularity>('monthly');
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dailyData, setDailyData] = useState<Record<string, DailyReturn>>({});
  const [loading, setLoading] = useState(true);
  const [showValues, setShowValues] = useState(true);
  const [showStarAssets, setShowStarAssets] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [histogramZoom, setHistogramZoom] = useState(0); 
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400);
  
  const histogramScrollRef = useRef<HTMLDivElement>(null);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const symbolHistoryRef = useRef<Record<string, Candle[]>>({});

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const years = useMemo(() => {
    if (transactions.length === 0) return [new Date().getFullYear()];
    const allYears = transactions.map(t => new Date(t.timestamp * 1000).getFullYear());
    const minYear = Math.min(...allYears);
    const maxYear = new Date().getFullYear();
    const range = [];
    for (let i = maxYear; i >= minYear; i--) range.push(i);
    return range.length > 0 ? range : [maxYear];
  }, [transactions]);

  useEffect(() => {
    if (chartAreaRef.current) {
        const observer = new ResizeObserver((entries) => {
            window.requestAnimationFrame(() => {
                if (entries[0]) {
                    setContainerWidth(entries[0].contentRect.width);
                    setContainerHeight(entries[0].contentRect.height);
                }
            });
        });
        observer.observe(chartAreaRef.current);
        return () => observer.disconnect();
    }
  }, [viewMode, isMaximized]);

  // Recalculate daily data when transactions or live prices change
  useEffect(() => {
    generateHistoryData();
  }, [transactions, currentPrices]);

  const generateHistoryData = async () => {
    if (transactions.length === 0) {
      setDailyData({}); 
      setLoading(false);
      return;
    }
    
    // Only set loading true if we don't have cached history yet (first load)
    if (Object.keys(symbolHistoryRef.current).length === 0) {
        setLoading(true);
    }

    try {
      const uniqueTickets: string[] = Array.from(new Set(transactions.map(t => t.ticket)));
      const minTimestamp = Math.min(...transactions.map(t => t.timestamp));
      
      // Fetch only if not already cached in ref
      const missingTickets = uniqueTickets.filter(t => !symbolHistoryRef.current[t]);
      
      if (missingTickets.length > 0) {
          const historyPromises = missingTickets.map((ticket: string) => {
            const sampleTx = transactions.find(t => t.ticket === ticket);
            const category = (sampleTx?.category || 'criptomoneda').toLowerCase();
            const assetType = category === 'acciones' ? 'stocks' : 'crypto';
            return fetchBinanceKlines(ticket, '1d', undefined, assetType);
          });
          
          const newKlines = await Promise.all(historyPromises);
          
          missingTickets.forEach((ticket, i) => {
             symbolHistoryRef.current[ticket] = newKlines[i];
          });
      }
      
      const symbolHistoryMap = symbolHistoryRef.current;

      const startDay = new Date(minTimestamp * 1000);
      startDay.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const historyMap: Record<string, DailyReturn> = {};
      let currentDay = new Date(startDay);
      
      const runningInventory: Record<string, number> = {};
      let runningNetCost = 0;

      const sortedTransactions = [...transactions].sort((a, b) => a.timestamp - b.timestamp);
      let transIndex = 0;

      while (currentDay <= today) {
        const dayStartTs = Math.floor(currentDay.getTime() / 1000);
        const dayEndTs = dayStartTs + 86399;
        
        // Check if the current iteration day matches today's date
        const isToday = currentDay.toDateString() === new Date().toDateString();

        while (transIndex < sortedTransactions.length && sortedTransactions[transIndex].timestamp <= dayEndTs) {
          const t = sortedTransactions[transIndex];
          const status = t.status.toLowerCase();
          const isBuy = status.includes('compra') || status.includes('buy');
          
          if (isBuy) {
            runningInventory[t.ticket] = (runningInventory[t.ticket] || 0) + t.quantity;
            runningNetCost += t.total;
          } else {
            runningInventory[t.ticket] = (runningInventory[t.ticket] || 0) - t.quantity;
            runningNetCost -= t.total;
          }
          transIndex++;
        }

        let dayValue = 0;
        const activeSignificantToday: string[] = [];
        
        Object.entries(runningInventory).forEach(([ticket, qty]) => {
          if (qty <= 0.00000001) return;
          
          const klines = symbolHistoryMap[ticket];
          // Default to historical kline logic
          let closePrice = 0;
          
          if (klines) {
              const kline = klines.find(k => k.time === dayStartTs) || 
                            [...klines].reverse().find(k => k.time < dayStartTs);
              if (kline) closePrice = kline.close;
          }

          // CRITICAL FIX: If calculating for "Today", override with live price if available
          if (isToday && currentPrices) {
             const binanceKey = ticket.replace(/[-/]/g, '').replace('USD', 'USDT');
             // Try direct match or binance key match
             const liveData = currentPrices[ticket] || currentPrices[binanceKey];
             if (liveData && liveData.price > 0) {
                 closePrice = liveData.price;
             }
          }
          
          if (closePrice > 0) {
             const assetValue = qty * closePrice;
             dayValue += assetValue;
             if (assetValue >= 2) {
                activeSignificantToday.push(ticket);
             }
          }
        });

        const dateKey = `${currentDay.getFullYear()}-${currentDay.getMonth()}-${currentDay.getDate()}`;
        historyMap[dateKey] = {
          date: dateKey,
          timestamp: dayStartTs,
          value: dayValue,
          cost: runningNetCost,
          pnl: dayValue - runningNetCost,
          roiPerc: runningNetCost > 0 ? ((dayValue - runningNetCost) / runningNetCost) * 100 : 0,
          heldTickers: activeSignificantToday
        };

        currentDay.setDate(currentDay.getDate() + 1);
      }

      setDailyData(historyMap);
    } catch (error) {
      console.error("Error reconstruyendo calendario:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTopTickerInPeriod = (startTimestamp: number, endTimestamp: number, periodROI: number, heldInPeriod: string[]) => {
    const historyMap = symbolHistoryRef.current;
    if (heldInPeriod.length === 0) return null;

    let targetTicker: string | null = null;
    let extremePerformance = periodROI >= 0 ? -Infinity : Infinity;

    heldInPeriod.forEach(ticker => {
        const klines = historyMap[ticker];
        
        // Find start price
        let startPrice = 0;
        if (klines && klines.length > 0) {
             const startCandle = klines.find(k => k.time >= startTimestamp) || klines[0];
             if (startCandle) startPrice = startCandle.close;
        }

        // Find end price - use Live Price if end is Today
        let endPrice = 0;
        const isEndToday = new Date(endTimestamp * 1000).toDateString() === new Date().toDateString();
        
        if (isEndToday && currentPrices) {
             const binanceKey = ticker.replace(/[-/]/g, '').replace('USD', 'USDT');
             const liveData = currentPrices[ticker] || currentPrices[binanceKey];
             if (liveData && liveData.price > 0) endPrice = liveData.price;
        }
        
        // Fallback to klines if not today or live price missing
        if (endPrice === 0 && klines && klines.length > 0) {
             const endCandle = [...klines].reverse().find(k => k.time <= endTimestamp) || klines[klines.length - 1];
             if (endCandle) endPrice = endCandle.close;
        }

        if (startPrice > 0 && endPrice > 0) {
            const performance = ((endPrice - startPrice) / startPrice) * 100;
            
            if (periodROI >= 0) {
                if (performance > extremePerformance) {
                    extremePerformance = performance;
                    targetTicker = ticker;
                }
            } else {
                if (performance < extremePerformance) {
                    extremePerformance = performance;
                    targetTicker = ticker;
                }
            }
        }
    });

    return targetTicker ? { ticker: targetTicker, performance: extremePerformance } : null;
  };

  const getDayROI = (year: number, month: number, day: number) => {
    const key = `${year}-${month}-${day}`;
    const current = dailyData[key];
    if (!current) return null;
    
    const prevDate = new Date(year, month, day - 1);
    const prevKey = `${prevDate.getFullYear()}-${prevDate.getMonth()}-${prevDate.getDate()}`;
    const prev = dailyData[prevKey];
    
    if (!prev || prev.value === 0) {
        return current.roiPerc; 
    }

    const pnlChange = current.pnl - prev.pnl;
    return (pnlChange / prev.value) * 100;
  };

  const getMonthStats = (year: number, month: number) => {
    const monthKeys = Object.keys(dailyData).filter(k => {
      const p = k.split('-');
      return parseInt(p[0]) === year && parseInt(p[1]) === month;
    });

    if (monthKeys.length === 0) return null;
    
    const lastDayKey = monthKeys.reduce((a, b) => dailyData[a].timestamp > dailyData[b].timestamp ? a : b);
    const firstDayKey = monthKeys.reduce((a, b) => dailyData[a].timestamp < dailyData[b].timestamp ? a : b);
    
    const lastDayData = dailyData[lastDayKey];
    const firstDayData = dailyData[firstDayKey];

    const prevDate = new Date(year, month, 0); 
    const prevMonthKey = `${prevDate.getFullYear()}-${prevDate.getMonth()}-${prevDate.getDate()}`;
    const prevData = dailyData[prevMonthKey];

    const startTs = firstDayData.timestamp;
    const endTs = lastDayData.timestamp;

    const heldThisMonth = Array.from(new Set(monthKeys.flatMap(k => dailyData[k].heldTickers)));

    let currentROI = 0;
    let currentPnL = 0;

    if (!prevData || prevData.value === 0) {
        currentROI = lastDayData.roiPerc;
        currentPnL = lastDayData.pnl;
    } else {
        const pnlChange = lastDayData.pnl - prevData.pnl;
        currentROI = (pnlChange / prevData.value) * 100;
        currentPnL = pnlChange;
    }

    const topStats = getTopTickerInPeriod(startTs, endTs, currentROI, heldThisMonth);

    return { roi: currentROI, pnl: currentPnL, topTicker: topStats?.ticker || null, topTickerROI: topStats?.performance || 0 };
  };

  const getYearStats = (year: number) => {
    const yearKeys = Object.keys(dailyData).filter(key => parseInt(key.split('-')[0]) === year);
    if (yearKeys.length === 0) return null;

    const lastKey = yearKeys.reduce((a, b) => dailyData[a].timestamp > dailyData[b].timestamp ? a : b);
    const lastData = dailyData[lastKey];
    const firstKey = yearKeys.reduce((a, b) => dailyData[a].timestamp < dailyData[b].timestamp ? a : b);
    const firstData = dailyData[firstKey];

    const heldThisYear = Array.from(new Set(yearKeys.flatMap(k => dailyData[k].heldTickers)));

    const prevYearKeys = Object.keys(dailyData).filter(key => parseInt(key.split('-')[0]) === year - 1);
    const prevKey = prevYearKeys.length > 0 ? prevYearKeys.reduce((a, b) => dailyData[a].timestamp > dailyData[b].timestamp ? a : b) : null;
    const prevData = prevKey ? dailyData[prevKey] : null;

    let currentROI = 0;
    let currentPnL = 0;

    if (!prevData || prevData.value < 1) {
        currentROI = lastData.roiPerc;
        currentPnL = lastData.pnl;
    } else {
        const pnlChange = lastData.pnl - prevData.pnl;
        currentROI = (pnlChange / prevData.value) * 100;
        currentPnL = pnlChange;
    }

    const topStats = getTopTickerInPeriod(firstData.timestamp, lastData.timestamp, currentROI, heldThisYear);

    return { roi: currentROI, topTicker: topStats?.ticker || null, topTickerROI: topStats?.performance || 0, pnl: currentPnL };
  };

  const getTotalHistoryStats = () => {
    const keys = Object.keys(dailyData);
    if (keys.length === 0) return null;
    const lastKey = keys.reduce((a, b) => dailyData[a].timestamp > dailyData[b].timestamp ? a : b);
    return { roi: dailyData[lastKey].roiPerc, pnl: dailyData[lastKey].pnl };
  };

  const histogramData = useMemo(() => {
    if (viewMode !== 'histogram') return [];
    
    let data: { label: string, roi: number, topTicker: string | null, topTickerROI: number }[] = [];
    
    if (histogramGranularity === 'yearly') {
        data = [...years].reverse().map(y => {
            const stats = getYearStats(y);
            return {
              label: y.toString(),
              roi: stats?.roi || 0,
              topTicker: stats?.topTicker || null,
              topTickerROI: stats?.topTickerROI || 0
            };
        });
    } else {
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);

        for (let y = minYear; y <= maxYear; y++) {
          for (let m = 0; m < 12; m++) {
            const stats = getMonthStats(y, m);
            if (stats !== null) {
              data.push({
                label: `${months[m].substring(0, 3)} ${y.toString().substring(2)}`,
                roi: stats.roi,
                topTicker: stats.topTicker,
                topTickerROI: stats.topTickerROI
              });
            }
          }
        }
    }

    const maxAbsROI = Math.max(...data.map(d => Math.abs(d.roi)), 0.1);
    const minHeight = 4; 
    const maxHeightPercent = 75; 
    
    return data.map(d => {
      const absRoi = Math.abs(d.roi);
      const rawRatio = absRoi / maxAbsROI;
      const amplifiedRatio = Math.sqrt(rawRatio); 
      
      return {
        ...d,
        relativeHeight: absRoi === 0 ? 0 : minHeight + (amplifiedRatio * (maxHeightPercent - minHeight))
      };
    });
  }, [viewMode, histogramGranularity, dailyData, years]);

  const minZoom = useMemo(() => {
    if (containerWidth === 0 || (viewMode === 'histogram' && histogramData.length === 0)) return 0.2;
    const baseWidth = 50;
    const gap = 4;
    const padding = 64;
    const availableWidth = containerWidth - padding;
    const count = viewMode === 'histogram' ? histogramData.length : 1; 
    const calculatedFitWidth = (availableWidth - (count - 1) * gap) / count;
    const finalFitWidth = Math.min(calculatedFitWidth, 120);
    const fitZoom = finalFitWidth / baseWidth;
    return Math.max(0.1, fitZoom);
  }, [containerWidth, histogramData.length, viewMode]);

  useEffect(() => {
    if (viewMode === 'histogram' && minZoom > 0.01) {
        if (Math.abs(histogramZoom - minZoom) > 0.05 || histogramZoom === 0) {
            setHistogramZoom(minZoom);
        }
    }
  }, [minZoom, viewMode, histogramGranularity]);

  const getCellStyle = (roi: number | null) => {
    if (roi === null) return {};
    const absRoi = Math.abs(roi);
    if (absRoi < 0.01) return {};
    
    const intensity = Math.min(Math.sqrt(absRoi / 10), 1); 
    const opacity = 0.20 + (intensity * 0.80);
    const isHighIntensity = opacity > 0.45;
    
    if (roi > 0) {
      return { 
        backgroundColor: `rgba(8, 153, 129, ${opacity})`, 
        color: isHighIntensity ? '#ffffff' : '#089981',
        boxShadow: isHighIntensity ? 'inset 0 0 10px rgba(255,255,255,0.1)' : 'none'
      };
    } else {
      return { 
        backgroundColor: `rgba(242, 54, 69, ${opacity})`, 
        color: isHighIntensity ? '#ffffff' : '#f23645',
        boxShadow: isHighIntensity ? 'inset 0 0 10px rgba(255,255,255,0.1)' : 'none'
      };
    }
  };

  const themeClasses = {
    panel: theme === 'dark' ? 'bg-[#161616] border-[#262626]' : 'bg-white border-slate-100',
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-500' : 'text-slate-400',
    btnActive: 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20',
    btnInactive: theme === 'dark' ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
    modalBg: theme === 'dark' ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-slate-200'
  };

  const getIconUrl = (symbol: string) => {
    const base = symbol.split(/[-/]/)[0].toUpperCase();
    return icons[base] || `https://assets.coincap.io/assets/icons/${base.toLowerCase()}@2x.png`;
  };

  const currentZoom = histogramZoom || minZoom;
  const finalWidth = 50 * currentZoom;
  const totalContentWidth = histogramData.length * finalWidth + (histogramData.length - 1) * 4;

  const footerStats = useMemo(() => {
    if (viewMode === 'histogram') {
        const stats = getTotalHistoryStats();
        return { label: 'RENDIMIENTO TOTAL HISTÓRICO', roi: stats?.roi || 0, pnl: stats?.pnl || 0 };
    }
    
    if (calendarStructure === 'days') {
        const stats = getYearStats(selectedYear);
        return { label: `RENDIMIENTO TOTAL ANUAL (${selectedYear})`, roi: stats?.roi || 0, pnl: stats?.pnl || 0 };
    } else {
        const stats = getTotalHistoryStats();
        return { label: 'RENDIMIENTO TOTAL HISTÓRICO', roi: stats?.roi || 0, pnl: stats?.pnl || 0 };
    }
  }, [viewMode, calendarStructure, selectedYear, dailyData]);

  return (
    <div className={`flex flex-col ${isMaximized ? 'h-full w-full p-2' : 'h-full space-y-6 animate-in fade-in duration-700'}`}>
      <div className={`flex flex-col flex-1 rounded-2xl border ${themeClasses.panel} shadow-2xl p-4 sm:p-6 overflow-hidden relative`}>
        
        <div className="flex flex-col lg:flex-row items-center justify-between mb-4 sm:mb-6 space-y-4 lg:space-y-0 flex-shrink-0 relative z-30">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
                <CalendarIcon size={20} className="text-indigo-500" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                   <h3 className={`text-base font-black uppercase tracking-tight ${themeClasses.textMain}`}>Rendimiento Temporal</h3>
                   <button 
                    onClick={() => setShowInfoModal(true)}
                    className="p-1 rounded-full hover:bg-white/5 transition-colors text-indigo-500/60 hover:text-indigo-500"
                   >
                      <Info size={14} className="animate-pulse" />
                   </button>
                </div>
                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Lógica de Comparación Punto a Punto</p>
              </div>
            </div>

            <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <button onClick={() => setViewMode('calendar')} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'calendar' ? themeClasses.btnActive : themeClasses.btnInactive}`}><LayoutGrid size={14} /><span>Calendario</span></button>
              <button onClick={() => setViewMode('histogram')} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'histogram' ? themeClasses.btnActive : themeClasses.btnInactive}`}><BarChart3 size={14} /><span>Histograma</span></button>
            </div>

            {viewMode === 'calendar' && calendarStructure === 'days' && (
              <div className="flex items-center space-x-2 bg-black/10 p-1 rounded-xl border border-white/5">
                <button onClick={() => setSelectedYear(y => y - 1)} className="p-1.5 hover:bg-white/10 rounded-lg transition-all text-slate-400"><ChevronLeft size={14} /></button>
                <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="bg-transparent text-[11px] font-black text-indigo-400 outline-none px-2 appearance-none text-center cursor-pointer">
                  {years.map(y => <option key={y} value={y} className="bg-[#1a1a1a] text-white">{y}</option>)}
                </select>
                <button onClick={() => setSelectedYear(y => y + 1)} className="p-1.5 hover:bg-white/10 rounded-lg transition-all text-slate-400"><ChevronRight size={14} /></button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {viewMode === 'calendar' && (
              <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                <button onClick={() => setCalendarStructure('days')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${calendarStructure === 'days' ? themeClasses.btnActive : themeClasses.btnInactive}`}>Días</button>
                <button onClick={() => setCalendarStructure('months')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${calendarStructure === 'months' ? themeClasses.btnActive : themeClasses.btnInactive}`}>Meses</button>
              </div>
            )}

            {viewMode === 'histogram' && (
              <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                <button onClick={() => setHistogramGranularity('monthly')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${histogramGranularity === 'monthly' ? themeClasses.btnActive : themeClasses.btnInactive}`}>Mensual %</button>
                <button onClick={() => setHistogramGranularity('yearly')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${histogramGranularity === 'yearly' ? themeClasses.btnActive : themeClasses.btnInactive}`}>Anual %</button>
              </div>
            )}

            <div className="flex items-center space-x-2">
              {viewMode === 'histogram' && (
                <div className={`flex items-center px-3 py-1.5 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'} space-x-3`}>
                    <Search size={14} className="text-slate-500" />
                    <button onClick={() => setHistogramZoom(z => Math.max(0.1, z - 0.05))} className="text-slate-500 hover:text-indigo-500 transition-all"><Minus size={12} /></button>
                    <input 
                      type="range" 
                      min="0.1" 
                      max="3.0" 
                      step="0.01" 
                      value={currentZoom} 
                      onChange={(e) => setHistogramZoom(parseFloat(e.target.value))}
                      className="w-24 h-1 bg-indigo-500/20 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                    <button onClick={() => setHistogramZoom(z => Math.min(3.0, z + 0.05))} className="text-slate-500 hover:text-indigo-500 transition-all"><Plus size={12} /></button>
                    <div className="w-[1px] h-3 bg-white/10 mx-1"></div>
                    <button onClick={() => setHistogramZoom(minZoom)} className="text-slate-500 hover:text-indigo-500 transition-all" title="Ajustar a ventana"><RotateCcw size={12} /></button>
                    <span className="text-[9px] font-black font-mono text-indigo-400 w-8">{Math.round(currentZoom * 100)}%</span>
                </div>
              )}

              {viewMode === 'histogram' && (
                <button 
                  onClick={() => setShowStarAssets(!showStarAssets)} 
                  className={`p-2 rounded-xl border transition-all ${showStarAssets ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'}`} 
                  title={showStarAssets ? "Ocultar Símbolos Estrella" : "Mostrar Símbolos Estrella"}
                >
                  <Medal size={16} />
                </button>
              )}

              <button onClick={() => setShowValues(!showValues)} className={`p-2 rounded-xl border transition-all ${showValues ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'}`} title="Ver Valores">{showValues ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => onToggleMaximize?.(!isMaximized)} className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 transition-all shadow-lg">{isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative" ref={chartAreaRef}>
          {viewMode === 'calendar' ? (
             <div className="h-full w-full flex flex-col overflow-hidden">
               {calendarStructure === 'days' ? (
                  <div key="days-view" className={`flex-1 flex flex-col animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-500 overflow-hidden`}>
                    <div className={`flex flex-col h-full w-full min-w-[900px]`}>
                      <div className="grid grid-cols-[70px_70px_80px_repeat(31,minmax(18px,1fr))] gap-0.5 mb-1 flex-shrink-0">
                        <div className="text-[10px] font-black uppercase text-slate-600 px-1 text-right pr-2">Total %</div>
                        <div className="text-[10px] font-black uppercase text-slate-600 px-1 text-right pr-2">Total $</div>
                        <div className="text-[10px] font-black uppercase text-slate-600 px-2">Mes</div>
                        {Array.from({ length: 31 }).map((_, i) => (
                          <div key={i} className="text-[10px] font-black uppercase text-slate-600 text-center">{i + 1}</div>
                        ))}
                      </div>

                      <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                        {months.map((month, mIdx) => {
                          const stats = getMonthStats(selectedYear, mIdx);
                          const monthROI = stats ? stats.roi : null;
                          const monthPnL = stats ? stats.pnl : null;
                          return (
                            <div key={month} className="grid grid-cols-[70px_70px_80px_repeat(31,minmax(18px,1fr))] gap-0.5 flex-1 min-h-0 group">
                              <div className={`text-[13px] font-mono font-black text-right pr-2 rounded-sm border flex items-center justify-end ${monthROI !== null ? (monthROI >= 0 ? 'text-[#089981] bg-[#089981]/5 border-[#089981]/20' : 'text-[#f23645] bg-[#f23645]/5 border-[#f23645]/20') : 'text-slate-600 border-transparent'}`}>
                                {monthROI !== null ? `${monthROI >= 0 ? '+' : ''}${monthROI.toFixed(1)}%` : '-'}
                              </div>
                              <div className={`text-[13px] font-mono font-black text-right pr-2 rounded-sm border flex items-center justify-end ${monthPnL !== null ? (monthPnL >= 0 ? 'text-[#089981] bg-[#089981]/5 border-[#089981]/20' : 'text-[#f23645] bg-[#f23645]/5 border-[#f23645]/20') : 'text-slate-600 border-transparent'}`}>
                                {monthPnL !== null ? `${monthPnL >= 0 ? '+' : '-'}${Math.abs(monthPnL).toLocaleString(undefined, {maximumFractionDigits:0})}` : '-'}
                              </div>
                              <div className={`text-[11px] font-black uppercase px-2 rounded-sm border transition-all flex items-center text-slate-400 border-transparent`}>{month}</div>
                              {Array.from({ length: 31 }).map((_, dIdx) => {
                                const day = dIdx + 1;
                                const roi = getDayROI(selectedYear, mIdx, day);
                                const isValidDate = new Date(selectedYear, mIdx, day).getMonth() === mIdx;
                                return (
                                  <div key={day} className={`flex-1 rounded-[2px] flex items-center justify-center text-[10px] font-black transition-all cursor-default ${!isValidDate ? 'opacity-0' : (roi === null ? (theme === 'dark' ? 'bg-[#1a1a1a]' : 'bg-slate-50') : '')}`} style={isValidDate ? getCellStyle(roi) : {}}>
                                    {roi !== null && <span className={`${showValues ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>{roi >= 0 ? '+' : ''}{roi.toFixed(1)}</span>}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
               ) : (
                  <div key="months-view" className={`flex-1 flex flex-col animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-500 overflow-hidden`}>
                      <div className={`flex flex-col h-full w-full min-w-[900px]`}>
                          <div className="grid grid-cols-[70px_70px_80px_repeat(12,minmax(60px,1fr))] gap-1 mb-2 flex-shrink-0">
                              <div className="text-[10px] font-black uppercase text-indigo-500 px-1 text-right pr-3 whitespace-nowrap">Total %</div>
                              <div className="text-[10px] font-black uppercase text-indigo-500 px-1 text-right pr-3 whitespace-nowrap">Total $</div>
                              <div className="text-[10px] font-black uppercase text-slate-500 px-2 text-center whitespace-nowrap">Año</div>
                              {months.map((m, i) => (
                                  <div key={i} className="text-[10px] font-black uppercase text-slate-600 text-center tracking-widest">{m.substring(0, 3)}</div>
                              ))}
                          </div>

                          <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                              {[...years].reverse().map((year) => {
                                  const yearStats = getYearStats(year);
                                  const yearROI = yearStats ? yearStats.roi : null;
                                  const yearPnL = yearStats ? yearStats.pnl : null;
                                  return (
                                      <div key={year} className="grid grid-cols-[70px_70px_80px_repeat(12,minmax(60px,1fr))] gap-1 flex-1 group min-h-0">
                                          <div className={`text-[13px] font-mono font-black text-right pr-3 rounded-xl border flex items-center justify-end ${yearROI !== null ? (yearROI >= 0 ? 'text-[#089981] bg-[#089981]/10 border-[#089981]/30 shadow-[0_0_15px_rgba(8,153,129,0.1)]' : 'text-[#f23645] bg-[#f23645]/10 border-[#f23645]/30 shadow-[0_0_15px_rgba(242,54,69,0.1)]') : 'text-slate-600 border-transparent'}`}>
                                              {yearROI !== null ? `${yearROI >= 0 ? '+' : ''}${yearROI.toFixed(1)}%` : '-'}
                                          </div>
                                          <div className={`text-[13px] font-mono font-black text-right pr-3 rounded-xl border flex items-center justify-end ${yearPnL !== null ? (yearPnL >= 0 ? 'text-[#089981] bg-[#089981]/10 border-[#089981]/30 shadow-[0_0_15px_rgba(8,153,129,0.1)]' : 'text-[#f23645] bg-[#f23645]/10 border-[#f23645]/30 shadow-[0_0_15px_rgba(242,54,69,0.1)]') : 'text-slate-600 border-transparent'}`}>
                                              {yearPnL !== null ? `${yearPnL >= 0 ? '+' : '-'}${Math.abs(yearPnL).toLocaleString(undefined, {maximumFractionDigits:0})}` : '-'}
                                          </div>
                                          <button 
                                              onClick={() => { setSelectedYear(year); setCalendarStructure('days'); }}
                                              className={`text-[13px] font-black uppercase px-2 rounded-xl border border-transparent transition-all flex items-center justify-center space-x-2 active:scale-95 group/year ${theme === 'dark' ? 'text-slate-400 hover:text-indigo-400 hover:bg-white/5' : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-100'}`}
                                          >
                                              <span>{year}</span>
                                              <ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover/year:opacity-100 group-hover/year:translate-x-0 transition-all" />
                                          </button>
                                          {Array.from({ length: 12 }).map((_, mIdx) => {
                                              const stats = getMonthStats(year, mIdx);
                                              const monthROI = stats ? stats.roi : null;
                                              return (
                                                  <div 
                                                      key={mIdx} 
                                                      className={`flex-1 rounded-xl border border-transparent flex flex-col items-center justify-center text-[12px] font-black transition-all cursor-default relative overflow-hidden group/cell ${monthROI === null ? (theme === 'dark' ? 'bg-white/5 border-white/5 opacity-30' : 'bg-slate-50 border-slate-100 opacity-30') : ''}`} 
                                                      style={getCellStyle(monthROI)}
                                                  >
                                                      {monthROI !== null && (
                                                          <>
                                                              <span className={`transition-all duration-300 ${showValues ? 'opacity-100' : 'opacity-0 group-hover/cell:opacity-100'}`}>{monthROI >= 0 ? '+' : ''}{monthROI.toFixed(1)}%</span>
                                                              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover/cell:opacity-100 pointer-events-none transition-opacity"></div>
                                                          </>
                                                      )}
                                                  </div>
                                              );
                                          })}
                                      </div>
                                  );
                              })}
                          </div>
                      </div>
                  </div>
               )}
             </div>
          ) : (
            <div className="h-full flex flex-col bg-black/5 rounded-2xl border border-white/5 overflow-hidden relative">
               <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-white/10 z-0 pointer-events-none"></div>
               <div ref={histogramScrollRef} className={`flex-1 overflow-x-auto overflow-y-hidden pt-24 pb-16 px-8 flex items-center ${totalContentWidth < containerWidth - 64 ? 'justify-center' : 'justify-start'} custom-scrollbar-horizontal relative z-10 space-x-1 [scrollbar-gutter:stable]`}>
                  {histogramData.map((item, i) => {
                    const isPositive = item.roi >= 0;
                    const isNarrow = currentZoom <= 0.7;
                    const labelText = isNarrow ? `${Math.round(item.roi)}` : `${isPositive ? '+' : ''}${item.roi.toFixed(1)}%`;
                    const labelFontSize = isNarrow ? 'text-[9px]' : 'text-[11px]';
                    const showBarLabel = currentZoom > 0.4;
                    const showTopIcon = currentZoom > 0.5 && item.topTicker && showStarAssets;
                    
                    const iconSize = Math.max(16, Math.min(28, 32 * currentZoom));

                    return (
                      <div key={i} className="flex flex-col items-center flex-shrink-0 group h-full justify-center" style={{ width: `${finalWidth}px` }}>
                        <div className="relative flex flex-col w-full flex-1 min-h-[120px]" style={{ height: `${containerHeight - 180}px` }}>
                          
                          {showTopIcon && (
                              <div className={`absolute ${isPositive ? 'top-2' : 'bottom-2'} left-1/2 -translate-x-1/2 z-40 flex flex-col items-center group/icon pointer-events-none`}>
                                  <div className={`opacity-0 group-hover/icon:opacity-100 transition-opacity duration-300 mb-2 pointer-events-none z-[60] w-28 absolute ${isPositive ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                                      <div className={`text-white text-[11px] font-black px-3 py-2 rounded-xl shadow-2xl flex flex-col items-center leading-tight border border-white/20 ${item.topTickerROI >= 0 ? 'bg-emerald-600' : 'bg-rose-600'}`}>
                                          <span className="uppercase tracking-tight mb-1">{item.topTicker?.split(/[-/]/)[0]}</span>
                                          <span className="text-[13px] font-mono border-t border-white/20 pt-1 w-full text-center">
                                            {item.topTickerROI >= 0 ? '+' : ''}{item.topTickerROI.toFixed(1)}%
                                          </span>
                                      </div>
                                  </div>
                                  <div 
                                    className="rounded-full border border-white/10 bg-[#161616] p-0.5 shadow-xl relative transition-transform duration-200 pointer-events-auto hover:scale-[2.2] hover:z-50 hover:shadow-indigo-500/60 cursor-pointer overflow-hidden flex items-center justify-center"
                                    style={{ 
                                      width: `${iconSize}px`, 
                                      height: `${iconSize}px`,
                                      transitionProperty: 'transform, box-shadow' 
                                    }}
                                  >
                                      <img 
                                        src={getIconUrl(item.topTicker!)} 
                                        className="w-full h-full object-contain pointer-events-none" 
                                        alt={item.topTicker!} 
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                      />
                                  </div>
                              </div>
                          )}

                          <div className="flex-1 flex flex-col justify-end relative">
                             {isPositive && (
                               <div className="w-full rounded-t-[4px] transition-all duration-700 bg-gradient-to-t from-[#089981] to-[#089981]/80 shadow-[0_0_20px_rgba(8,153,129,0.2)] group-hover:opacity-100 opacity-90 relative" style={{ height: `${item.relativeHeight}%` }}>
                                 {showValues && (
                                   <div className={`absolute bottom-full left-0 right-0 mb-2 text-center font-black font-mono transition-opacity ${showValues ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} text-[#089981] ${labelFontSize}`}>
                                     {labelText}
                                   </div>
                                 )}
                               </div>
                             )}
                          </div>
                          <div className="flex-1 flex flex-col justify-start relative">
                             {!isPositive && (
                               <div className="w-full rounded-b-[4px] transition-all duration-700 bg-gradient-to-b from-[#f23645] to-[#f23645]/80 shadow-[0_0_20px_rgba(242,54,69,0.2)] group-hover:opacity-100 opacity-90 relative" style={{ height: `${item.relativeHeight}%` }}>
                                 {showValues && (
                                   <div className={`absolute top-full left-0 right-0 mt-2 text-center font-black font-mono transition-opacity ${showValues ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} text-[#f23645] ${labelFontSize}`}>
                                     {labelText}
                                   </div>
                                 )}
                               </div>
                             )}
                          </div>
                        </div>
                        <div className={`mt-10 pt-3 border-t ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'} w-full text-center flex-shrink-0`}>
                            {showBarLabel ? (
                                <span className={`text-[9px] font-black uppercase tracking-widest ${themeClasses.textMuted}`}>{item.label}</span>
                            ) : (
                                <div className="w-1 h-1 bg-slate-700 rounded-full mx-auto"></div>
                            )}
                        </div>
                      </div>
                    );
                  })}
               </div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between flex-shrink-0 relative z-20">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded bg-[#089981]"></div><span className="text-[9px] font-black text-slate-500 uppercase">Ganancia Mercado</span></div>
            <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded bg-[#f23645]"></div><span className="text-[9px] font-black text-slate-500 uppercase">Pérdida Mercado</span></div>
            <div className="flex items-center space-x-2 border-l border-white/10 pl-6 ml-2">
                <div className="w-4 h-4 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div></div>
                <span className="text-[9px] font-black text-slate-500 uppercase">Activo Estrella (Máxima Influencia)</span>
            </div>
          </div>
          <div className="flex items-center space-x-6">
              <div className="text-right">
                <span className="block text-[8px] font-black text-slate-600 uppercase tracking-widest">
                  {footerStats.label}
                </span>
                <div className={`flex items-center justify-end space-x-2 ${footerStats.roi >= 0 ? 'text-[#089981]' : 'text-[#f23645]'}`}>
                    <span className="text-lg font-black font-mono">
                      {footerStats.roi >= 0 ? '+' : ''}{footerStats.roi.toFixed(1)}%
                    </span>
                    <span className="text-sm font-bold opacity-80">
                        ({footerStats.pnl >= 0 ? '+' : '-'}${Math.abs(footerStats.pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                    </span>
                </div>
              </div>
          </div>
        </div>
      </div>

      {showInfoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
           <div className={`relative w-full max-w-md rounded-[2rem] border shadow-2xl p-8 overflow-hidden animate-in zoom-in-95 duration-300 ${themeClasses.modalBg}`}>
              <div className="absolute top-0 right-0 p-6">
                 <button onClick={() => setShowInfoModal(false)} className="text-slate-500 hover:text-indigo-500 transition-colors">
                    <X size={24} />
                 </button>
              </div>

              <div className="mb-8">
                 <div className="flex items-center space-x-3 mb-2">
                    <div className="p-2 bg-indigo-500/10 rounded-xl">
                       <Info size={20} className="text-indigo-500" />
                    </div>
                    <h2 className={`text-xl font-black uppercase tracking-tight ${themeClasses.textMain}`}>Guía de Rendimiento</h2>
                 </div>
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-white/5 pb-4">Entendiendo la Lógica de Cálculo</p>
              </div>

              <div className="space-y-6">
                 <div className="flex items-start space-x-4">
                    <div className="p-2 rounded-lg bg-[#089981]/10 text-[#089981]">
                       <Activity size={18} />
                    </div>
                    <div>
                       <h4 className={`text-xs font-black uppercase tracking-widest mb-1 ${themeClasses.textMain}`}>Valor Diario</h4>
                       <p className="text-[11px] leading-relaxed text-slate-500">Es la variación porcentual del mercado de un día para otro. Compara el cambio en el <strong>PnL</strong> dividido por el <strong>Valor Total</strong> de la cartera del día anterior.</p>
                    </div>
                 </div>

                 <div className="flex items-start space-x-4">
                    <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500">
                       <PieChart size={18} />
                    </div>
                    <div>
                       <h4 className={`text-xs font-black uppercase tracking-widest mb-1 ${themeClasses.textMain}`}>Total Mensual</h4>
                       <p className="text-[11px] leading-relaxed text-slate-500">Métrica punto a punto. Compara el rendimiento del último día del mes contra el cierre del mes anterior. <strong>Neutraliza nuevas inversiones</strong>.</p>
                    </div>
                 </div>

                 <div className="flex items-start space-x-4">
                    <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
                       <Medal size={18} />
                    </div>
                    <div>
                       <h4 className={`text-xs font-black uppercase tracking-widest mb-1 ${themeClasses.textMain}`}>Activo Estrella</h4>
                       <p className="text-[11px] leading-relaxed text-slate-500">Es el activo que más influyó en el resultado: el que más subió en periodos positivos o el que más cayó en periodos negativos (solo activos con más de $2 en cartera).</p>
                    </div>
                 </div>
              </div>

              <div className="mt-10 pt-6 border-t border-white/5 text-center">
                 <button 
                  onClick={() => setShowInfoModal(false)}
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-600/20 transition-all active:scale-95"
                 >
                    Entendido
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioCalendar;
